import "./styles/main.css"
import type { ComponentPublicInstance } from "vue"
import { createApp } from "vue"
import type { RouteRecordRaw } from "vue-router"
import { createRouter, createWebHistory } from "vue-router"
import { createHead } from "@vueuse/head"
import Popper from "vue3-popper"

import { routes as pageRoutes } from "vue-router/auto-routes"
import App from "./App.vue"
import { initAuth } from "./utils/auth"
import useLiveChat from "./utils/live-chat"
import { isProd } from "./utils/environment"
import api from "./utils/api"
import { until } from "@vueuse/core"
import FloatingVue from "floating-vue"
import "floating-vue/dist/style.css"
import Gleap from "gleap"
import Toast, { POSITION } from "vue-toastification"
import "vue-toastification/dist/index.css"
import { createPinia } from "pinia"
import VueConfetti from "vue-confetti"
import { posthog } from "posthog-js"
import { initDittofeed, dittofeed } from "./utils/dittofeed"

const app = createApp(App)
app.component("Popper", Popper)
app.use(FloatingVue)
app.use(Toast, {
    position: POSITION.TOP_RIGHT,
})
app.use(VueConfetti)
app.provide("confetti", app.config.globalProperties.$confetti)

const pinia = createPinia()
app.use(pinia)

const routes: RouteRecordRaw[] = [...pageRoutes, { path: "/", redirect: "/campaigns" }]

export const history = createWebHistory()
export const router = createRouter({ history, routes })

router.afterEach(() => {
    const url = new URL(window.location.href)

    window.analytics?.page()

    posthog.capture("page_view", {
        path: url.pathname,
        search: url.search,
        url: url.href,
        referrer: document.referrer,
    })

    dittofeed.track("page_view", {
        path: url.pathname,
        search: url.search,
        url: url.href,
        referrer: document.referrer,
    })

    Gleap.trackEvent("trackPage", {
        page: url.pathname,
        url: url.href,
        referrer: document.referrer,
    })

    // check cookie permissions before sending data to pixels
    const { purposes = {} } = window._iub?.cs?.api?.getPreferences() ?? {}
    if (purposes?.["5"]) {
        window.fbq?.("track", "PageView")
        window.gtag?.("event", "page_view", {
            page_title: document.title,
            page_location: window.location,
        })
    }
})
app.use(router)

// if we've been unable to fetch a new route, it's likely there's been a release and we should just navigate there by refreshing
router.onError((error, to) => {
    console.warn("Navigation onError - assuming a release has occurred and reloading.", error)
    const currentPath = window.location.pathname + window.location.search + window.location.hash

    if (to.fullPath !== currentPath) {
        window.location.href = to.fullPath
    } else {
        // prevent a redirect loop in case we've miscategorised a genuine error
        const url = new URL(window.location.href)
        const noRedirect = url.searchParams.get("noredirect") === "true"
        if (noRedirect) {
            console.warn("Redirect prevented due to noredirect flag")
            console.error("Uncaught Navigation Error", error)
            return
        }

        const targetPath = to.fullPath
        const separator = targetPath.includes("?") ? "&" : "?"
        const newHref = `${targetPath}${separator}noredirect=true`

        if (newHref !== currentPath) {
            window.location.href = newHref
        } else {
            window.location.reload()
        }
    }
})

const head = createHead()
app.use(head)

async function main() {
    Gleap.initialize("vPRkPJFdtCTQfWrDXK8wgi7L1i8ZZBuE")

    // analytics
    until(() => window.analytics !== undefined)
        .toBeTruthy()
        .then(() => {
            const SEGMENT_WRITE_KEY = isProd()
                ? "a0qPSNtsZxWudyFpa2oD7t5lPgr7B7HC" // production
                : "NUw9I4Cc7pmUkxmHHpwJtIJawMkB0ZlI" // staging

            window.analytics!.load(SEGMENT_WRITE_KEY)

            window.analytics!.page()
        })

    app.config.errorHandler = (
        error: any,
        instance: ComponentPublicInstance | null,
        info: string,
    ) => {
        console.error("App Error", error)
    }

    app.config.warnHandler = (
        msg: string,
        instance: ComponentPublicInstance | null,
        trace: string,
    ) => {
        console.warn("App Warning", msg, trace)
    }

    await initAuth(api)
    app.mount("#app")

    // initDittofeed()
    useLiveChat()
}

main()
